// src/pages/GalleryPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import api from "../services/httpService";
import {
  X,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
  Camera,
  LayoutGrid,
  Grid3x3,
} from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "./main/footer";
import "./Gallery.css";

const GalleryPage = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryImagesHero, setGalleryImagesHero] = useState(null);
 // const [galleryImagesHeroSection, setGalleryImagesHeroSection] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("masonry");
  const [currentPage, setCurrentPage] = useState(1);
  const [imagesPerPage, setImagesPerPage] = useState(12);
  const [isMobile, setIsMobile] = useState(false);
  const AWS_IMAGE_URL = process.env.REACT_APP_AWS_IMAGE_URL || "";

  // Detect screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch theme colors
  useEffect(() => {
    const fetchThemeColors = async () => {
      try {
         const res = await api.get("/sentinix/dashboardsettings");
        const data = res.data;
        
        if (data?.success && data.data) {
          const allData = Array.isArray(data.data) ? data.data : [data.data];
          const latest = allData.sort((a, b) => b.id - a.id)[0];
          
          if (latest) {
            const color1 = latest.color1 || "#f83600";
            const color2 = latest.color2 || "#fe8c00";

            document.documentElement.style.setProperty("--theme-color1", color1);
            document.documentElement.style.setProperty("--theme-color2", color2);
            document.documentElement.style.setProperty("--theme-color1-rgb", hexToRgb(color1));
            document.documentElement.style.setProperty("--theme-color2-rgb", hexToRgb(color2));
          }
        }
      } catch (error) {
        console.error("Error fetching theme colors:", error);
      }
    };
    
    fetchThemeColors();
  }, []);

  // Helper to convert hex to rgb
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? 
      `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : 
      '248, 54, 0';
  };

  useEffect(() => {
    const getGalleryImages = async () => {
      try {
        const res = await api.get("/sentinix/galleries");
        const data = res.data;

        console.log("Gallery API Response:", data);

        if (data?.success && Array.isArray(data.data)) {
          const galleryItems = data.data.filter(
            (item) => item.type === "gallery" && !item.isDeleted
          );

          const allImages = galleryItems.flatMap(
            (item) => item.imageKeys || []
          );

          const uniqueImages = [...new Set(allImages)];
          setGalleryImages(uniqueImages);

          const heroItem = data.data.find(
            (item) => item.type === "hero" && !item.isDeleted
          );
          
          //setGalleryImagesHeroSection(heroItem || null);
          if (heroItem?.imageKeys?.length) {
            setGalleryImagesHero(heroItem.imageKeys[0]);
          }
        }
      } catch (error) {
        console.error("Gallery API Error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getGalleryImages();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [imagesPerPage]);

  const totalPages = Math.ceil(galleryImages.length / imagesPerPage);
  const startIndex = (currentPage - 1) * imagesPerPage;
  const endIndex = startIndex + imagesPerPage;
  const currentImages = galleryImages.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openLightbox = (globalIndex) => {
    setSelectedImage(globalIndex);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = "auto";
  };

const nextImage = useCallback(() => {
  if (selectedImage !== null) {
    setSelectedImage((prev) => (prev + 1) % galleryImages.length);
  }
}, [selectedImage, galleryImages.length]);

const prevImage = useCallback(() => {
  if (selectedImage !== null) {
    setSelectedImage((prev) =>
      (prev - 1 + galleryImages.length) % galleryImages.length
    );
  }
}, [selectedImage, galleryImages.length]);

 useEffect(() => {
  const handleKeyPress = (e) => {
    if (selectedImage === null) return;
    if (e.key === "ArrowRight") nextImage();
    if (e.key === "ArrowLeft") prevImage();
    if (e.key === "Escape") closeLightbox();
  };

  window.addEventListener("keydown", handleKeyPress);
  return () => window.removeEventListener("keydown", handleKeyPress);
}, [selectedImage, nextImage, prevImage]);

  const getMasonryHeight = (index) => {
    if (isMobile) {
      const heights = ["200px", "240px", "220px", "260px"];
      return heights[index % heights.length];
    }
    const heights = ["280px", "350px", "310px", "380px", "300px", "340px"];
    return heights[index % heights.length];
  };

  const getPaginationNumbers = () => {
    const delta = isMobile ? 0 : 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    if (isMobile && totalPages > 5) {
      if (currentPage <= 3) {
        return [1, 2, 3, '...', totalPages];
      } else if (currentPage >= totalPages - 2) {
        return [1, '...', totalPages - 2, totalPages - 1, totalPages];
      } else {
        return [1, '...', currentPage, '...', totalPages];
      }
    }

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  return (
    <>
      <Navbar />
      <div className="gallery-page">
        {/* Hero Section */}
        <section className="svc-hero">
          {galleryImagesHero ? (
            <motion.img
              src={`${AWS_IMAGE_URL}${galleryImagesHero}`}
              alt="Gallery Hero"
              className="gallery-hero-img"
              initial={{ scale: 1.3 }}
              animate={{ scale: 1 }}
              transition={{
                duration: 1.2,
                ease: "easeOut"
              }}
              onError={(e) => {
                e.target.src = '/placeholder-image.png';
              }}
            />
          ) : (
            <div className="gallery-hero-placeholder">
              <Camera className="placeholder-icon" />
            </div>
          )}

          {/* <div className="gallery-hero-content">
            <motion.h1
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
              className="gallery-hero-title"
            >
              {galleryImagesHeroSection?.title || "SENTINIX TECH SOLUTIONS"}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.25, ease: "easeOut" }}
              className="gallery-hero-description"
            >
              {galleryImagesHeroSection?.description || "Experience Innovation. Experience Sentinix."}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
              className="gallery-hero-tagline"
            >
              Innovate. Integrate. Inspire.
            </motion.p>

            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.55, ease: "easeOut" }}
              className="gallery-hero-line"
            />
          </div> */}

          <div className="gallery-scroll-indicator">
            {/* <div className="scroll-dot" /> */}
          </div>
        </section>

        {/* Controls Section */}
        <section className="gallery-controls">
          <div className="gallery-controls-container">
            <div className="gallery-controls-row">
              {/* View Mode Toggle */}
              <div className="view-mode-toggle">
                <button
                  onClick={() => setViewMode("masonry")}
                  className={`view-mode-btn ${viewMode === "masonry" ? "active" : ""}`}
                  title="Masonry View"
                >
                  <LayoutGrid className="icon-sm" />
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`view-mode-btn ${viewMode === "grid" ? "active" : ""}`}
                  title="Grid View"
                >
                  <Grid3x3 className="icon-sm" />
                </button>
              </div>

              {/* Per Page + Total */}
              <div className="gallery-stats">
                <div className="per-page-selector">
                  <span className="per-page-label">Per page:</span>
                  <select
                    value={imagesPerPage}
                    onChange={(e) => setImagesPerPage(Number(e.target.value))}
                    className="per-page-select"
                  >
                    <option value={9}>9</option>
                    <option value={12}>12</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <span className="total-count">
                  Total: <strong>{galleryImages.length}</strong>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="gallery-grid-section">
          <div className="gallery-grid-container">
            {isLoading ? (
              <div className="loading-state">
                <div className="spinner-container">
                  <div className="spinner"></div>
                  <div className="spinner-reverse"></div>
                </div>
                <p className="loading-text">Loading gallery...</p>
              </div>
            ) : galleryImages.length === 0 ? (
              <div className="empty-state">
                <Camera className="empty-icon" />
                <h3 className="empty-title">No Images Yet</h3>
                <p className="empty-description">
                  Check back soon for amazing content!
                </p>
              </div>
            ) : (
              <>
                <div
                  className={
                    viewMode === "masonry"
                      ? "gallery-masonry"
                      : "gallery-grid-view"
                  }
                >
                  {currentImages.map((imageKey, idx) => {
                    const globalIndex = startIndex + idx;
                    return (
                      <div
                        key={globalIndex}
                        className="gallery-item"
                        style={{
                          height:
                            viewMode === "masonry"
                              ? getMasonryHeight(globalIndex)
                              : "auto",
                        }}
                        onClick={() => openLightbox(globalIndex)}
                      >
                        <div className="gallery-item-inner">
                          <div className="gallery-item-hover-bg"></div>
                          <div className="gallery-item-image-wrapper">
                            <img
                              src={`${AWS_IMAGE_URL}${imageKey}`}
                              alt={`Gallery ${globalIndex + 1}`}
                              className="gallery-item-image"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = '/placeholder-image.png';
                              }}
                            />
                            <div className="gallery-item-overlay"></div>
                            <div className="gallery-item-zoom">
                              <div className="zoom-icon-wrapper">
                                <div className="zoom-icon-bg"></div>
                                <div className="zoom-icon">
                                  <ZoomIn className="icon-sm" />
                                </div>
                              </div>
                            </div>
                            <div className="gallery-item-badge">
                              <div className="badge-bg"></div>
                              <div className="badge-text">
                                #{globalIndex + 1}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination-container">
                    <div className="pagination">
                      {/* Previous */}
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`pagination-prev ${currentPage === 1 ? 'disabled' : ''}`}
                      >
                        <ChevronLeft className="icon-sm" />
                        <span className="prev-text">Previous</span>
                        <span className="prev-text-mobile">Prev</span>
                      </button>

                      {/* Page Numbers */}
                      <div className="pagination-numbers">
                        {getPaginationNumbers().map((page, idx) => 
                          page === '...' ? (
                            <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
                              ...
                            </span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                            >
                              {page}
                            </button>
                          )
                        )}
                      </div>

                      {/* Next */}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`pagination-next ${currentPage === totalPages ? 'disabled' : ''}`}
                      >
                        <span className="next-text">Next</span>
                        <span className="next-text-mobile">Next</span>
                        <ChevronRight className="icon-sm" />
                      </button>
                    </div>

                    {/* Page Info */}
                    <p className="pagination-info">
                      Showing <strong>{startIndex + 1}</strong>–
                      <strong>{Math.min(endIndex, galleryImages.length)}</strong> of{" "}
                      <strong>{galleryImages.length}</strong> images
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Lightbox */}
        {selectedImage !== null && (
          <div
            className="lightbox-overlay"
            onClick={closeLightbox}
          >
            {/* Header */}
            <div className="lightbox-header">
              <div className="lightbox-header-content">
                <div className="lightbox-counter">
                  <span className="counter-current">{selectedImage + 1}</span>
                  <span className="counter-separator">/</span>
                  <span className="counter-total">{galleryImages.length}</span>
                </div>
                <button
                  onClick={closeLightbox}
                  className="lightbox-close"
                >
                  <X className="icon-md" />
                </button>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="lightbox-nav lightbox-prev"
            >
              <ChevronLeft className="icon-md" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="lightbox-nav lightbox-next"
            >
              <ChevronRight className="icon-md" />
            </button>

            {/* Image */}
            <div
              className="lightbox-image-container"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lightbox-image-wrapper">
                <img
                  src={`${AWS_IMAGE_URL}${galleryImages[selectedImage]}`}
                  alt={`Gallery ${selectedImage + 1}`}
                  className="lightbox-image"
                  onError={(e) => {
                    e.target.src = '/placeholder-image.png';
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default GalleryPage;